/**
 * Top Orgs table for the Execution Ledger monitor.
 *
 * Lives in the master/detail grid's RIGHT column when no execution is
 * selected (`ExecutionLedger.tsx` renders this in place of the detail
 * pane). When an execution IS selected, the detail pane takes over and
 * this disappears — the table is a discovery surface, not load-bearing
 * info while you're inspecting a specific row.
 *
 * Server-bounded to ≤10 rows (lexserver `executionStats.topOrgsAgg`
 * $limit:10) so the height is naturally capped.
 */

import type { ExecutionLedgerStatsResult } from "@/types/execution-ledger";

interface Props {
  topOrgs: ExecutionLedgerStatsResult["topOrgs"];
  loading: boolean;
  /** Click handler — fires when the operator clicks an org row to narrow
   *  the active filter to that org. Operators don't memorize ULID-shaped
   *  IDs at thousand-execution scale; this is the one-click scope-narrow
   *  surface that makes the free-text Org id input unnecessary for the
   *  common case. */
  onOrgClick: (orgId: string) => void;
  /** Currently-selected org filter — highlights the matching row. */
  activeOrgId?: string;
}

export function ExecutionLedgerTopOrgs({
  topOrgs,
  loading,
  onOrgClick,
  activeOrgId,
}: Props) {
  return (
    <div
      className="flex min-h-0 flex-col overflow-hidden rounded-md border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900"
      data-testid="execution-ledger-top-orgs"
    >
      <div className="border-b border-slate-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:border-slate-700 dark:text-slate-400">
        Top orgs {!loading && `(${topOrgs.length})`}
        <span className="ml-2 font-normal normal-case tracking-normal text-slate-400">
          — click to filter
        </span>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-xs text-slate-400">Loading…</div>
        ) : topOrgs.length === 0 ? (
          <div className="p-4 text-center text-xs text-slate-400">
            No executions in the current window.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              <tr>
                <th className="px-3 py-1.5 text-left font-medium">Org</th>
                <th className="px-3 py-1.5 text-right font-medium">Count</th>
                <th className="px-3 py-1.5 text-right font-medium">Cost</th>
              </tr>
            </thead>
            <tbody>
              {topOrgs.map((o) => {
                const active = activeOrgId === o.orgId;
                return (
                  <tr
                    key={o.orgId}
                    onClick={() => onOrgClick(o.orgId)}
                    className={`cursor-pointer border-t border-slate-100 dark:border-slate-800 ${
                      active
                        ? "bg-indigo-50 dark:bg-indigo-950"
                        : "hover:bg-slate-50 dark:hover:bg-slate-800"
                    }`}
                    data-testid={`execution-ledger-top-orgs-row-${o.orgId}`}
                  >
                    <td className="px-3 py-1.5 font-mono">{o.orgId}</td>
                    <td className="px-3 py-1.5 text-right font-mono">
                      {fmtCount(o.count)}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono">
                      {fmtCost(o.costUsd)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <div className="border-t border-slate-200 px-3 py-1.5 text-[11px] italic text-slate-400 dark:border-slate-700">
        Select an execution to see its details.
      </div>
    </div>
  );
}

function fmtCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function fmtCost(usd: number): string {
  if (usd === 0) return "$0";
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
}
